
import { TELEPORT_THRESHOLD_MM } from "../client/prediction";
import type { World } from "../sim/collision";
import { FRAC_ONE, cosFx, sinFx } from "../sim/fixed";
import { type MoveState, type MoveStats, groundSpeedMmS } from "../sim/movement";

export const CORRECTION_HALF_LIFE_MS = 120;

export const MAX_ERROR_MM = 225;

export const LEASH_PAYOUT_MS = 1_000;

/** The local player's drawn position: one that moves every frame. */
export class SelfMotion {
  private xMm = 0;
  private yMm = 0;
  /** Nothing has been drawn yet, so the first frame arrives rather than eases
   *  in from the origin. */
  private placed = false;

  /** Put the body somewhere with no easing: spawn, and every crossing. */
  snapTo(state: MoveState): void {
    this.xMm = state.xMm;
    this.yMm = state.yMm;
    this.placed = true;
  }

  follow(target: MoveState, stats: MoveStats, world: World, dtMs: number): MoveState {
    if (!this.placed || distanceMm(this.xMm, this.yMm, target) > TELEPORT_THRESHOLD_MM) {
      this.snapTo(target);
      return drawn(target, target.xMm, target.yMm);
    }
    if (dtMs > 0) {
      const fromXMm = this.xMm;
      const fromYMm = this.yMm;
      // 1. Dead reckoning -- `__AccumulationMovement`.
      const speedMmS = groundSpeedMmS(target, stats, world);
      let deadReckonedMm = 0;
      if (speedMmS > 0) {
        deadReckonedMm = (speedMmS * dtMs) / 1000;
        this.xMm += (deadReckonedMm * cosFx(target.heading)) / FRAC_ONE;
        this.yMm += (deadReckonedMm * sinFx(target.heading)) / FRAC_ONE;
      }
      // 2. Correction -- `MovementProcess` closing on the destination.
      const closed = 1 - Math.pow(0.5, dtMs / CORRECTION_HALF_LIFE_MS);
      this.xMm += (target.xMm - this.xMm) * closed;
      this.yMm += (target.yMm - this.yMm) * closed;
      // 3. The leash, paid out rather than stepped. See `MAX_ERROR_MM`.
      const error = distanceMm(this.xMm, this.yMm, target);
      if (error > MAX_ERROR_MM) {
        const excess = error - MAX_ERROR_MM;
        const pull = Math.min(excess, Math.max(deadReckonedMm, (excess * dtMs) / LEASH_PAYOUT_MS));
        const keep = (error - pull) / error;
        this.xMm = target.xMm + (this.xMm - target.xMm) * keep;
        this.yMm = target.yMm + (this.yMm - target.yMm) * keep;
      }
      if (deadReckonedMm > 0) {
        const along = ((this.xMm - fromXMm) * cosFx(target.heading)
          + (this.yMm - fromYMm) * sinFx(target.heading)) / FRAC_ONE;
        if (along < 0) {
          this.xMm -= (along * cosFx(target.heading)) / FRAC_ONE;
          this.yMm -= (along * sinFx(target.heading)) / FRAC_ONE;
        }
      }
    }
    return drawn(target, this.xMm, this.yMm);
  }
}

/** The predicted state with the drawn position substituted. Heading and gait
 *  are the prediction's own: which way the body faces and what it is doing are
 *  decided by the input that produced them, not by this smoothing. */
function drawn(target: MoveState, xMm: number, yMm: number): MoveState {
  return { xMm, yMm, heading: target.heading, gait: target.gait };
}

function distanceMm(xMm: number, yMm: number, to: MoveState): number {
  return Math.hypot(to.xMm - xMm, to.yMm - yMm);
}
